import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

function getSubdomainInfo() {
  const hostname = window.location.hostname;
  const isLocalhost = hostname === 'localhost';
  const isLocalhostSubdomain = hostname.endsWith('.localhost') && !isLocalhost;
  const isMainApp = isLocalhost || hostname === 'app.getassura.app' || hostname === 'app-staging.getassura.app';
  const isStagingSubdomain = hostname.endsWith('.app-staging.getassura.app');
  const isProductionSubdomain = hostname.endsWith('.getassura.app') && !isMainApp && !isStagingSubdomain;
  let currentSlug = null;
  if (isLocalhostSubdomain) currentSlug = hostname.replace('.localhost', '');
  else if (isStagingSubdomain) currentSlug = hostname.replace('.app-staging.getassura.app', '');
  else if (isProductionSubdomain) currentSlug = hostname.replace('.getassura.app', '');
  return { isMainApp, isLocalhost, isLocalhostSubdomain, isProductionSubdomain, isStagingSubdomain, currentSlug };
}

function buildRedirectUrl(slug) {
  const { isLocalhost, isLocalhostSubdomain, isStagingSubdomain } = getSubdomainInfo();
  const hostname = window.location.hostname;
  const normalizedSlug = slug.replace(/-/g, '');
  if (isLocalhost || isLocalhostSubdomain) {
    return `http://${normalizedSlug}.localhost:3000/login`;
  }
  if (isStagingSubdomain || hostname === 'app-staging.getassura.app') {
    return `https://${normalizedSlug}.app-staging.getassura.app/login`;
  }
  return `https://${normalizedSlug}.getassura.app/login`;
}

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("email"); // "email" | "password"
  const [orgName, setOrgName] = useState(null);
  const [orgNotFound, setOrgNotFound] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // On tenant subdomains — validate the subdomain on mount
  useEffect(() => {
    const { isMainApp, currentSlug } = getSubdomainInfo();
    if (isMainApp || !currentSlug) return;
    axios.get(`${API_URL}/api/auth/org/${currentSlug}`)
      .then(res => setOrgName(res.data.name))
      .catch(() => setOrgNotFound(true));
  }, []);

  // Step 1 — check email and redirect to correct subdomain if needed
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { isMainApp, currentSlug } = getSubdomainInfo();
      if (isMainApp) {
        // Look up which org this email belongs to
        const res = await axios.get(`${API_URL}/api/auth/org-by-email?email=${encodeURIComponent(email)}`);
        const orgSlug = res.data.slug;
        const normalizedSlug = orgSlug.replace(/-/g, '');
        // If super_admin or no org found, stay on main app
        if (!orgSlug) {
          setStep("password");
          return;
        }
        // Check if already on correct subdomain
        if (currentSlug && currentSlug.replace(/-/g, '') === normalizedSlug) {
          setStep("password");
          return;
        }
        // Redirect to correct subdomain with email pre-filled
        const redirectUrl = buildRedirectUrl(orgSlug);
        window.location.href = `${redirectUrl}?email=${encodeURIComponent(email)}`;
        return;
      }
      // Already on a subdomain — proceed to password step
      setStep("password");
    } catch (err) {
      // User not found or no org — allow to proceed to password (backend will reject invalid creds)
      setStep("password");
    } finally {
      setLoading(false);
    }
  };

  // Step 2 — submit password and log in
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.mfa_required) {
        if (result?.mfa_enforced) {
          localStorage.setItem("mfa_setup_token", result.mfa_token);
          navigate("/mfa/setup", { state: { enforced: true, message: result.message } });
        } else {
          navigate("/mfa/verify", { state: { mfa_token: result.mfa_token } });
        }
        return;
      }
      toast.success("Welcome back!");
      navigate("/dashboard", { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
      setLoading(false);
    }
  };

  // Pre-fill email from query param (set by redirect from main app)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get("email");
    if (emailParam) {
      setEmail(emailParam);
      setStep("password");
    }
  }, []);

  if (orgNotFound) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-2">Organization Not Found</h1>
          <p className="text-slate-400">The organization you're looking for doesn't exist or has been deactivated.</p>
          <a href="https://app.getassura.app/login" className="mt-6 inline-block text-blue-400 hover:text-blue-300 text-sm">
            Go to main login →
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      {/* INNER CONTAINER - Navy background with grid pattern and glows */}
      <div
        className="w-full max-w-6xl h-screen lg:h-auto lg:rounded-3xl lg:shadow-2xl lg:overflow-hidden flex flex-col lg:flex-row relative"
        style={{
          backgroundImage: `
            linear-gradient(0deg, transparent 24%, rgba(150,190,255,.55) 25%, rgba(150,190,255,.55) 26%, transparent 27%, transparent 74%, rgba(150,190,255,.55) 75%, rgba(150,190,255,.55) 76%, transparent 77%, transparent),
            linear-gradient(90deg, transparent 24%, rgba(150,190,255,.55) 25%, rgba(150,190,255,.55) 26%, transparent 27%, transparent 74%, rgba(150,190,255,.55) 75%, rgba(150,190,255,.55) 76%, transparent 77%, transparent)
          `,
          backgroundSize: "50px 50px",
          backgroundColor: "#0f1b2e",
        }}
      >
        {/* Glow overlays - hidden on mobile/tablet */}
        <div className="hidden lg:block absolute top-0 right-[30rem] w-56 h-56 bg-blue-500/30 rounded-full blur-3xl pointer-events-none z-20" />
        <div className="hidden lg:block absolute bottom-[1rem] left-[2rem] w-56 h-56 bg-blue-600/25 rounded-full blur-3xl pointer-events-none z-20" />

        {/* LEFT PANEL - 60% width on desktop */}
        <div className="hidden lg:flex lg:w-3/5 flex-col justify-between p-16 text-white relative z-10">
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-[#0a1628]/95 z-0" />
          {/* Content wrapper */}
          <div className="relative z-10 flex flex-col justify-between h-full">
            {/* Logo */}
            <div className="flex items-center gap-3 mb-12">
              <img src="/assura/icononly_transparent.png" alt="Assura" className="h-16 w-16" />
              <span className="text-2xl font-bold tracking-wide">ASSURA</span>
            </div>

            {/* Hero Copy */}
            <div className="flex-1 flex flex-col justify-center">
              <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-8">
                Compliance
                <br />
                <span className="text-[#378ADD]">without the</span>
                <br />
                complexity.
              </h1>
              <p className="text-base text-slate-400 leading-relaxed max-w-md">
                Caribbean-grade data protection compliance. Manage risk, respond to breaches, and stay audit-ready — all
                in one place.
              </p>
            </div>

            {/* Jurisdiction Badges */}
            <div className="flex flex-wrap gap-3 mt-auto">
              <div className="px-3 py-1.5 border-2 border-[#378ADD]/50 rounded-full text-xs font-medium text-slate-300 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#378ADD]" />
                T&T DPA 2011
              </div>
              <div className="px-3 py-1.5 border-2 border-[#378ADD]/50 rounded-full text-xs font-medium text-slate-300 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#378ADD]" />
                Jamaica DPA 2020
              </div>
              <div className="px-3 py-1.5 border-2 border-[#378ADD]/50 rounded-full text-xs font-medium text-slate-300 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#378ADD]" />
                Cayman DPL
              </div>
              <div className="px-3 py-1.5 border-2 border-[#378ADD]/50 rounded-full text-xs font-medium text-slate-300 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#378ADD]" />
                Bermuda PIPA
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL - 40% width on desktop, full width on mobile - WHITE BACKGROUND */}
        <div className="flex-1 lg:w-2/5 bg-white flex items-center justify-center p-6 lg:p-12 relative z-10">
          <div className="w-full max-w-sm">
            {/* Logo for mobile */}
            <div className="lg:hidden mb-8 flex items-center gap-3 justify-center">
              <img src="/assura/icononly_transparent.png" alt="Assura" className="h-10 w-10" />
              <span className="font-bold text-gray-900 text-lg">ASSURA</span>
            </div>

            {/* Form Header */}
            <div className="mb-8 text-center lg:text-left">
              <h2 className="text-3xl lg:text-2xl font-bold text-gray-900 mb-2">Welcome back</h2>
              <p className="text-sm text-gray-600">
                {orgName ? `Sign in to ${orgName}` : "Sign in to your Assura workspace"}
              </p>
            </div>

            {/* Step 1 — Email */}
            {step === "email" && (
              <form onSubmit={handleEmailSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700 block mb-2">
                    Work email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    data-testid="login-email-input"
                    className="w-full h-11 px-4 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  variant="primary"
                  size="lg"
                  className="w-full mt-8"
                  data-testid="login-submit-btn"
                >
                  {loading ? "Checking..." : "Continue"}
                </Button>
              </form>
            )}

            {/* Step 2 — Password */}
            {step === "password" && (
              <form onSubmit={handlePasswordSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700 block mb-2">
                    Work email
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      readOnly
                      className="w-full h-11 px-4 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 text-sm"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-blue-600 hover:text-blue-700 text-xs shrink-0"
                      onClick={() => setStep("email")}
                    >
                      Change
                    </Button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="password" className="text-sm font-medium text-gray-700 block mb-2">
                    Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoFocus
                      data-testid="login-password-input"
                      className="w-full h-11 px-4 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                      data-testid="toggle-password-visibility"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <div className="text-right pt-1">
                  <Link
                    to="/forgot-password"
                    className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  variant="primary"
                  size="lg"
                  className="w-full mt-8"
                  data-testid="login-submit-btn"
                >
                  {loading ? "Signing in..." : "Sign in"}
                </Button>
              </form>
            )}

            {/* No Account Section */}
            <div className="mt-8 text-center">
              <p className="text-sm text-gray-600 mb-4">
                No account?
                <Link to="/request-access" className="text-blue-600 hover:text-blue-700 font-medium ml-1">
                  Request access
                </Link>
              </p>

              {/* Footer Links */}
              <p className="text-xs text-gray-600">
                © 2026 Assura.
                <Link to="/privacy" className="text-blue-600 hover:text-blue-700 font-medium ml-2">
                  Privacy
                </Link>
                <span> · </span>
                <Link to="/terms" className="text-blue-600 hover:text-blue-700 font-medium">
                  Terms
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
